import { CheckInviteTokenDto, RegisterUserDto } from '@pharmac-extranet/extranet-api'
import {
  ControlledInput,
  ControlledMultiSelect,
  ExtranetContainer,
  emailInputPattern,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButton,
  PharmacContainer,
  PharmacErrorToast,
  PharmacFieldset,
  PharmacH3,
  PharmacH5,
  PharmacH6,
  PharmacIcon,
  PharmacLabel,
  PharmacLink,
  PharmacSuccessToast,
  PharmacText,
  PharmacTooltip,
  usePharmacToast,
} from '@pharmac/ui'
import { UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useRef, useState } from 'react'
import { Control, FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { ApprovalSwitch } from '../../components/user/ApprovalSwitch'
import { __PRIVACY_POLICY_LINK, __TERMS_OF_USE_LINK } from '../../constants'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { api } from '../../services/HttpService'
import { isValidInviteToken } from '../../utils/validate-token'

type Props = {
  cancelButton: () => void
}

export const RegistrationView = ({ cancelButton }: Props) => {
  const { key } = useLanguage()
  const { minDesktop, maxMobile } = useBreakpoint()
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordAgain, setShowPasswordAgain] = useState(false)
  const queryClient = useQueryClient()
  const toastHook = usePharmacToast()
  const { doLogin } = useAuthenticationContext()

  const handleShowPassword = () => setShowPassword(!showPassword)
  const handleShowPasswordAgain = () => setShowPasswordAgain(!showPasswordAgain)

  const { t } = useLanguage()

  const { token } = useParams()

  const userNameInputRules = {
    required: true,
    minLength: 5,
    maxLength: 32,
    pattern: /^\S*$/,
  }

  const passWordInputRules = {
    required: true,
    minLength: 15,
    pattern: /(?=.*?[A-Z])(?=.*?[a-z])(?=.*?\d)(?=.*?[^a-zA-Z0-9])/,
  }

  const getInviteTokenInfo = (token?: string): UseQueryOptions<CheckInviteTokenDto> => ({
    queryFn: async () => await api.customers.getInviteTokenInfo(token as string),
    queryKey: ['__inviteTokenInfo'],
    enabled: isValidInviteToken(token),
  })

  const { data: tokenInfo } = useQuery(getInviteTokenInfo(token))

  const { mutate: registerUser } = useMutation({
    mutationFn: (registerUser: { token: string; dto: RegisterUserDto }) =>
      api.customers.registerUserWithInviteToken(registerUser.token, registerUser.dto),
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>
            {t('extranet:inviteLinks.registration.newUserSuccess')}
          </PharmacSuccessToast>
        ),
      })

      queryClient.invalidateQueries({
        queryKey: ['__inviteTokenInfo'],
      })

      doLogin({ redirectUri: window.location.origin })
    },
    onError: async (error) => {
      const isConflictError = isAxiosError(error) && error.response?.status === 409
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacErrorToast>
            {isConflictError
              ? t('extranet:inviteLinks.registration.newUserNameConflict')
              : t('extranet:inviteLinks.registration.newUserError')}
          </PharmacErrorToast>
        ),
      })
    },
  })

  const handleRegisterUser = (dto: RegisterUserDto) => {
    registerUser({ token: String(token), dto: dto })
  }

  const formhook = useForm<RegisterUserDto>({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: tokenInfo?.userEmail,
      countryCode: '',
      username: '',
      password: '',
      certificateCardNumber: '',
      countryName: '',
      phone: '',
      postCode: '',
      streetName: '',
      townName: '',
      cookiePolicyApproval: false,
      marketingAuthorization: false,
      privacyPolicyApproval: false,
      termsOfUseApproval: false,
      professionalRoleIds: [],
      professionalRolesSelected: true,
    },
  })

  const password = useRef({})

  password.current = formhook.watch('password')

  const { data: professionalRoles } = useQuery({
    queryKey: ['__professionalRoles'],
    queryFn: async () => await api.customers.geUserProfessionalRoles(),
  })

  return (
    <FormProvider {...formhook}>
      <div
        style={{
          width: minDesktop ? '960px' : 'fit-content',
          height: minDesktop ? '90%' : '85%',
          marginBottom: minDesktop ? '' : '5rem',
          borderRadius: '21px',
          overflow: 'hidden',
        }}
      >
        <ExtranetContainer
          marginLargeTop={!minDesktop}
          full
          flexColumn
          style={{
            height: minDesktop ? '100%' : '95%',
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--color-primary) var(--color-accent-2)',
            backgroundColor: 'var(--color-accent-2)',
            borderRadius: '18px',
          }}
        >
          <ExtranetContainer
            full
            flexColumn
            paddingLarge
            paddingSmallBottom
          >
            <PharmacH3 color="purple">{t('extranet:inviteLinks.registration.title')}</PharmacH3>
            <PharmacText paddingLargeTop>
              {t('extranet:inviteLinks.registration.subtitle')}
            </PharmacText>
          </ExtranetContainer>
          <ExtranetContainer
            full
            flexColumn
            paddingSmallTop
            paddingLarge
            paddingSmall={maxMobile}
          >
            <PharmacFieldset
              style={{ width: '100%', marginTop: 'var(--spacing-large)', borderRadius: '21px' }}
            >
              <ExtranetContainer
                full
                flexRow={minDesktop}
                flexColumn={!minDesktop}
              >
                <ExtranetContainer
                  full
                  flexColumn
                >
                  <PharmacContainer
                    paddingMediumBottom
                    style={minDesktop ? { width: '95%' } : { width: '100%' }}
                  >
                    <ControlledInput
                      name="lastName"
                      rules={{ required: true }}
                      requiredError={t('common:requiredFieldError')}
                      control={formhook.control as unknown as Control<FieldValues>}
                      label={t('common:lastName')}
                    />
                  </PharmacContainer>

                  <PharmacContainer
                    paddingMediumBottom
                    style={minDesktop ? { width: '95%' } : { width: '100%' }}
                  >
                    <ControlledInput
                      name="firstName"
                      rules={{ required: true }}
                      requiredError={t('common:requiredFieldError')}
                      control={formhook.control as unknown as Control<FieldValues>}
                      label={t('common:firstName')}
                    />
                  </PharmacContainer>
                  <PharmacContainer
                    paddingMediumBottom
                    style={minDesktop ? { width: '95%' } : { width: '100%' }}
                  >
                    <ExtranetContainer
                      style={{ width: 'fit-content' }}
                      flexRow
                      alignCenter
                      paddingSmallBottom
                    >
                      <PharmacLabel>{t('common:emailAddress')}*</PharmacLabel>
                      <PharmacTooltip
                        paddingSmallLeft
                        maxWidth={!minDesktop ? '295px' : '600px'}
                        handle={
                          <PharmacIcon
                            style={{ color: 'var(--color-info)' }}
                            size="18px"
                            icon="info"
                          />
                        }
                      >
                        <PharmacText>
                          {t('extranet:inviteLinks.registration.emailTooltip')}
                        </PharmacText>
                      </PharmacTooltip>
                    </ExtranetContainer>
                    <ControlledInput
                      name="email"
                      rules={{ required: true, pattern: emailInputPattern }}
                      patternError={t('common:emailPatternError')}
                      requiredError={t('common:requiredFieldError')}
                      control={formhook.control as unknown as Control<FieldValues>}
                      disabled
                    />
                  </PharmacContainer>
                  <PharmacContainer
                    paddingMediumBottom
                    style={minDesktop ? { width: '95%' } : { width: '100%' }}
                  >
                    <ControlledInput
                      name="phone"
                      control={formhook.control as unknown as Control<FieldValues>}
                      label={t('common:phoneNumber')}
                    />
                  </PharmacContainer>
                </ExtranetContainer>

                <ExtranetContainer
                  full
                  flexColumn
                >
                  <PharmacContainer
                    paddingMediumBottom
                    style={minDesktop ? { width: '95%' } : { width: '100%' }}
                  >
                    <ControlledInput
                      name="streetName"
                      control={formhook.control as unknown as Control<FieldValues>}
                      label={t('common:streetName')}
                    />
                  </PharmacContainer>
                  <PharmacContainer
                    paddingMediumBottom
                    style={minDesktop ? { width: '95%' } : { width: '100%' }}
                  >
                    <ControlledInput
                      name="postCode"
                      control={formhook.control as unknown as Control<FieldValues>}
                      label={t('common:postCode')}
                    />
                  </PharmacContainer>
                  <PharmacContainer
                    paddingMediumBottom
                    style={minDesktop ? { width: '95%' } : { width: '100%' }}
                  >
                    <ControlledInput
                      name="townName"
                      control={formhook.control as unknown as Control<FieldValues>}
                      label={t('common:townName')}
                    />
                  </PharmacContainer>
                  <PharmacContainer
                    paddingMediumBottom
                    style={minDesktop ? { width: '95%' } : { width: '100%' }}
                  >
                    <ExtranetContainer
                      style={{ width: 'fit-content' }}
                      flexRow
                      alignCenter
                      paddingSmallBottom
                    >
                      <PharmacLabel>{t('common:countryCode')}*</PharmacLabel>
                      <PharmacTooltip
                        paddingSmallLeft
                        maxWidth={!minDesktop ? '295px' : '600px'}
                        handle={
                          <PharmacIcon
                            style={{ color: 'var(--color-info)' }}
                            size="18px"
                            icon="info"
                          />
                        }
                      >
                        <PharmacText>
                          {t('extranet:inviteLinks.registration.countryTooltip')}
                        </PharmacText>
                      </PharmacTooltip>
                    </ExtranetContainer>
                    <ControlledInput
                      rules={{ required: true }}
                      requiredError={t('common:requiredFieldError')}
                      name="countryCode"
                      control={formhook.control as unknown as Control<FieldValues>}
                    />
                  </PharmacContainer>
                </ExtranetContainer>
              </ExtranetContainer>
            </PharmacFieldset>

            <ExtranetContainer
              full
              flexRow={minDesktop}
              flexColumn={!minDesktop}
            >
              <PharmacFieldset
                style={{
                  width: minDesktop ? '50%' : '100%',
                  height: '95%',
                  marginTop: 'var(--spacing-medium)',
                  marginRight: 'var(--spacing-medium)',
                  borderRadius: '21px',
                }}
              >
                <ExtranetContainer flexColumn>
                  <PharmacContainer
                    paddingMediumBottom
                    style={{ width: '100%' }}
                  >
                    <ExtranetContainer
                      style={{ width: 'fit-content' }}
                      flexRow
                      alignCenter
                      paddingSmallBottom
                    >
                      <PharmacLabel>{t('common:userName')}*</PharmacLabel>
                      <PharmacTooltip
                        paddingSmallLeft
                        maxWidth={!minDesktop ? '295px' : '600px'}
                        handle={
                          <PharmacIcon
                            style={{ color: 'var(--color-info)' }}
                            size="18px"
                            icon="info"
                          />
                        }
                      >
                        <PharmacText>
                          {t('extranet:inviteLinks.registration.userNameTooltip')}
                        </PharmacText>
                      </PharmacTooltip>
                    </ExtranetContainer>
                    <ControlledInput
                      autoComplete="off"
                      placeholder={t('extranet:inviteLinks.registration.usernamePlaceholder')}
                      name="username"
                      rules={{ ...userNameInputRules }}
                      maxLengthError={t('extranet:inviteLinks.registration.usernameMaxLengthError')}
                      minLengthError={t('extranet:inviteLinks.registration.usernameMinLengthError')}
                      requiredError={t('common:requiredFieldError')}
                      patternError={t('extranet:inviteLinks.registration.usernamePatternError')}
                      control={formhook.control as unknown as Control<FieldValues>}
                    />
                  </PharmacContainer>

                  <PharmacContainer
                    paddingMediumBottom
                    style={{ width: '100%' }}
                  >
                    <ExtranetContainer
                      style={{ width: 'fit-content' }}
                      flexRow
                      alignCenter
                      paddingSmallBottom
                    >
                      <PharmacLabel>{t('common:password')}*</PharmacLabel>
                      <PharmacTooltip
                        paddingSmallLeft
                        maxWidth={!minDesktop ? '295px' : '600px'}
                        handle={
                          <PharmacIcon
                            style={{ color: 'var(--color-info)' }}
                            size="18px"
                            icon="info"
                          />
                        }
                      >
                        <PharmacText>
                          {t('extranet:inviteLinks.registration.passwordTooltip')}
                        </PharmacText>
                      </PharmacTooltip>
                    </ExtranetContainer>
                    <ControlledInput
                      autoComplete="off"
                      type={showPassword ? 'text' : 'password'}
                      posticon={
                        <ExtranetContainer
                          onClick={handleShowPassword}
                          marginMediumRight
                        >
                          <PharmacIcon
                            size="16px"
                            icon={showPassword ? 'eyeOff' : 'eye'}
                          />
                        </ExtranetContainer>
                      }
                      placeholder={t('extranet:inviteLinks.registration.passwordPlaceholder')}
                      name="password"
                      rules={{
                        ...passWordInputRules,
                      }}
                      minLengthError={t('extranet:inviteLinks.registration.passwordMinLengthError')}
                      requiredError={t('common:requiredFieldError')}
                      patternError={t('extranet:inviteLinks.registration.passwordPatternError')}
                      control={formhook.control as unknown as Control<FieldValues>}
                    />
                  </PharmacContainer>
                  <PharmacContainer
                    paddingMediumBottom
                    style={{ width: '100%' }}
                  >
                    <ControlledInput
                      autoComplete="off"
                      type={showPasswordAgain ? 'text' : 'password'}
                      posticon={
                        <ExtranetContainer
                          onClick={handleShowPasswordAgain}
                          marginMediumRight
                        >
                          <PharmacIcon
                            size="16px"
                            icon={showPasswordAgain ? 'eyeOff' : 'eye'}
                          />
                        </ExtranetContainer>
                      }
                      placeholder={t('extranet:inviteLinks.registration.passwordPlaceholder')}
                      name="passwordAgain"
                      rules={{
                        required: true,
                        validate: (value: any) => !!value && value === password.current,
                      }}
                      minLengthError={t('extranet:inviteLinks.registration.passwordMinLengthError')}
                      requiredError={t('common:requiredFieldError')}
                      patternError={t('extranet:inviteLinks.registration.passwordPatternError')}
                      validateError={t('extranet:inviteLinks.registration.passwordsDoNotMatch')}
                      control={formhook.control as unknown as Control<FieldValues>}
                      label={t('extranet:inviteLinks.registration.passwordAgain')}
                    />
                  </PharmacContainer>
                </ExtranetContainer>
              </PharmacFieldset>

              {!minDesktop && (
                <PharmacFieldset
                  style={{
                    width: '100%',
                    marginTop: 'var(--spacing-medium)',
                    borderRadius: '21px',
                  }}
                >
                  <ExtranetContainer
                    full
                    flexRow={minDesktop}
                    flexColumn={!minDesktop}
                  >
                    <PharmacContainer
                      paddingMediumBottom
                      style={minDesktop ? { width: '50%' } : { width: '100%' }}
                    >
                      <ControlledInput
                        name="certificateCardNumber"
                        control={formhook.control as unknown as Control<FieldValues>}
                        label={t('common:certificateCardNumber')}
                      />
                    </PharmacContainer>
                  </ExtranetContainer>
                </PharmacFieldset>
              )}

              <PharmacFieldset
                style={{
                  width: minDesktop ? '50%' : '100%',
                  height: '95%',
                  marginTop: 'var(--spacing-medium)',
                  borderRadius: '21px',
                }}
              >
                <ExtranetContainer flexColumn>
                  <PharmacH5 paddingLargeBottom>
                    {t('extranet:inviteLinks.registration.selectProfessionalRoleTitle')}
                  </PharmacH5>
                  <PharmacText paddingLargeBottom>
                    {t('extranet:inviteLinks.registration.selectProfessionalRoleInfo')}
                  </PharmacText>
                </ExtranetContainer>
                <ControlledMultiSelect
                  control={formhook.control as unknown as Control<FieldValues>}
                  name="professionalRoleIds"
                >
                  {professionalRoles?.map((role) => (
                    <option
                      key={role.id}
                      value={role.id}
                    >
                      {key(role.displayName)}
                    </option>
                  ))}
                </ControlledMultiSelect>
              </PharmacFieldset>
            </ExtranetContainer>

            {minDesktop && (
              <PharmacFieldset
                style={{ width: '100%', marginTop: 'var(--spacing-medium)', borderRadius: '21px' }}
              >
                <ExtranetContainer
                  full
                  flexRow={minDesktop}
                  flexColumn={!minDesktop}
                >
                  <PharmacContainer
                    paddingMediumBottom
                    style={minDesktop ? { width: '50%' } : { width: '100%' }}
                  >
                    <ControlledInput
                      name="certificateCardNumber"
                      control={formhook.control as unknown as Control<FieldValues>}
                      label={t('common:certificateCardNumber')}
                    />
                  </PharmacContainer>
                </ExtranetContainer>
              </PharmacFieldset>
            )}
            <PharmacFieldset
              style={{ width: '100%', marginTop: 'var(--spacing-medium)', borderRadius: '21px' }}
            >
              <ExtranetContainer
                full
                flexRow={minDesktop}
                flexColumn={!minDesktop}
              >
                <ExtranetContainer
                  third={minDesktop}
                  flexColumn
                >
                  <PharmacLabel paddingSmallBottom>{t('extranet:user.userRoles')}:</PharmacLabel>
                  {tokenInfo?.roles?.map((a) => (
                    <PharmacText
                      paddingSmallBottom
                      key={a.id}
                    >
                      {t(`customer.roles.${a.role}`)}
                    </PharmacText>
                  ))}
                </ExtranetContainer>
              </ExtranetContainer>
            </PharmacFieldset>
            <ExtranetContainer
              paddingLargeTop
              paddingLargeLeft={minDesktop}
              flexColumn
              full
            >
              <ApprovalSwitch
                name="marketingAuthorization"
                control={formhook.control as unknown as Control<FieldValues>}
                label={t('extranet:user.marketingAuthorization')}
                onChange={() => null}
                marginLargeBottom
              >
                <PharmacText paddingSmallTop>
                  {t('extranet:inviteLinks.registration.marketingAuthorizationInfo')}
                </PharmacText>
              </ApprovalSwitch>
              <ApprovalSwitch
                rules={{ required: true }}
                label={`${t('extranet:user.privacyPolicy')}*`}
                name="privacyPolicyApproval"
                control={formhook.control as unknown as Control<FieldValues>}
                onChange={() => null}
                marginLargeBottom
              >
                <PharmacText
                  paddingSmallTop
                  paddingMediumBottom
                >
                  {t('extranet:inviteLinks.registration.privacyPolicyInfo')}
                </PharmacText>
                <PharmacLink
                  href={__PRIVACY_POLICY_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PharmacH6
                    style={{ textDecoration: 'underline', display: 'flex', flexDirection: 'row' }}
                    paddingSmallRight
                    color="purple"
                  >
                    {t('extranet:inviteLinks.registration.extranetPrivacyPolicy')}

                    <div style={{ marginRight: 'var(--spacing-small)' }}></div>
                    <PharmacIcon
                      stroke="purple"
                      size="16px"
                      icon="externalLink"
                    />
                  </PharmacH6>
                </PharmacLink>
              </ApprovalSwitch>
              <ApprovalSwitch
                rules={{ required: true }}
                label={`${t('extranet:user.termsOfUse')}*`}
                name="termsOfUseApproval"
                control={formhook.control as unknown as Control<FieldValues>}
                onChange={() => null}
                marginLargeBottom
              >
                <PharmacText
                  paddingSmallTop
                  paddingMediumBottom
                >
                  {t('extranet:user.termsOfUseInfo')}
                </PharmacText>
                <PharmacLink
                  href={__TERMS_OF_USE_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PharmacH6
                    style={{ textDecoration: 'underline', display: 'flex', flexDirection: 'row' }}
                    paddingSmallRight
                    color="purple"
                  >
                    {t('extranet:inviteLinks.registration.extranettermsOfUse')}
                    <div style={{ marginRight: 'var(--spacing-small)' }}></div>
                    <PharmacIcon
                      stroke="purple"
                      size="16px"
                      icon="externalLink"
                    />
                  </PharmacH6>
                </PharmacLink>
              </ApprovalSwitch>
            </ExtranetContainer>
          </ExtranetContainer>
          <ExtranetContainer
            style={{ borderTop: '1.2px solid var(--color-accent-4)' }}
            paddingLarge
            full
            justifyEnd={minDesktop}
            justifyCenter={!minDesktop}
          >
            <PharmacButton
              fullWidth={!minDesktop}
              variant="secondary"
              marginLargeRight
              onClick={cancelButton}
            >
              {t('common:cancel')}
            </PharmacButton>
            <PharmacButton
              fullWidth={!minDesktop}
              onClick={formhook.handleSubmit(handleRegisterUser)}
              disabled={!formhook.formState.isValid}
            >
              {t('common:save')}
            </PharmacButton>
          </ExtranetContainer>
        </ExtranetContainer>
      </div>
    </FormProvider>
  )
}
